
import React, { createContext, useContext, useState, useEffect } from 'react';
import { SubscriptionTier } from '../types';
import { useToast } from './ToastContext';
import { useNavigate } from 'react-router-dom';
import { getCurrentOrganisationId, supabase, isMockMode } from '../services/supabaseClient';
import { getPlanConfig, normalizePlanId } from '../config/pricing';

interface SubscriptionContextType {
  tier: SubscriptionTier;
  setTier: (tier: SubscriptionTier) => void;
  features: {
    aiExtraction: boolean;
    bulkUpload: boolean;
    riskScoring: boolean;
    advancedReports: boolean;
    aiAppeals: boolean;
    concierge: boolean;
    dvlaLookup: boolean;
    agreementAudit: boolean;
    multipleTeamMembers: boolean;
  };
  checkAccess: (feature: keyof SubscriptionContextType['features']) => boolean;
  promptUpgrade: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Feature flags configuration. Legacy pro/managed values are normalized so the
// UI does not keep two plan models alive.
const TIER_FEATURES = {
  trial: {
    aiExtraction: true,
    bulkUpload: true,
    riskScoring: true,
    advancedReports: false,
    aiAppeals: false,
    concierge: false,
    dvlaLookup: false,
    agreementAudit: false,
    multipleTeamMembers: false,
  },
  starter: {
    aiExtraction: true,
    bulkUpload: true,
    riskScoring: true,
    advancedReports: false,
    aiAppeals: false,
    concierge: false,
    dvlaLookup: false,
    agreementAudit: false,
    multipleTeamMembers: false, // Starter limited to 1 team member
  },
  pro: getPlanConfig('pro_fleet').features,
  managed: getPlanConfig('concierge').features,
  growth: getPlanConfig('growth').features,
  pro_fleet: getPlanConfig('pro_fleet').features,
  concierge: getPlanConfig('concierge').features,
};

const getTierFeatures = (tier: SubscriptionTier) => {
  if (tier === 'trial') return TIER_FEATURES.trial;
  return getPlanConfig(tier).features;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Fix: Use lazy initialization to read from localStorage immediately. 
  // This prevents the state from defaulting to 'starter' on initial render and then switching, 
  // or failing to persist if the effect runs out of order.
  const [tier, setTier] = useState<SubscriptionTier>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('pcn_fleet_tier');
      return (saved as SubscriptionTier) || 'starter';
    }
    return 'starter';
  });

  const { addToast } = useToast();
  const navigate = useNavigate();

  const updateTier = (newTier: SubscriptionTier) => {
    setTier(newTier);
    localStorage.setItem('pcn_fleet_tier', newTier);
  };

  // Source of truth in production: organisations.subscription_tier
  // (localStorage remains useful for demo/mock mode)
  useEffect(() => {
    if (isMockMode) return;

    let active = true;

    const loadTierFromServer = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const orgId = await getCurrentOrganisationId();
        const { data, error } = await supabase
          .from('organisations')
          .select('subscription_tier')
          .eq('id', orgId)
          .maybeSingle();

        if (error) throw error;

        const serverTier = (data?.subscription_tier as SubscriptionTier) || 'starter';
        if (!active) return;
        updateTier(serverTier === 'trial' ? 'trial' : normalizePlanId(serverTier));
      } catch (err) {
        console.error('Failed to load subscription tier:', err);
      }
    };

    void loadTierFromServer();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (!session) {
        updateTier('starter');
        return;
      }
      void loadTierFromServer();
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkAccess = (feature: keyof typeof TIER_FEATURES['starter']): boolean => {
    return getTierFeatures(tier)[feature];
  };

  const promptUpgrade = () => {
    addToast("This feature unlocks after upgrading from the trial or Starter plan.", "info");
    navigate('/pricing');
  };

  return (
    <SubscriptionContext.Provider value={{
      tier,
      setTier: updateTier,
      features: getTierFeatures(tier),
      checkAccess,
      promptUpgrade
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
